import { useEffect, useState } from 'react';
import PageLayout from '@/components/layout/PageLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Copy, Plus, Trash2 } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { chatbotService, type ChatSettings, type ChatStyleConfig } from '@/services/chatbotService';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { projectService, type ProjectDto } from '@/services/projectService';
import { Switch } from '@/components/ui/switch';

type EmbedChoice = 'bubble' | 'iframe';

const Chatbot = () => {
  const [chatbotId, setChatbotId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [embedChoice, setEmbedChoice] = useState<EmbedChoice>('bubble');
  const [settings, setSettings] = useState<ChatSettings | null>(null);
  const [loadingSettings, setLoadingSettings] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('TEAM');

  useEffect(() => {
    (async () => {
      try {
        // Clear previous state when switching scope so UI reflects absence while loading
        setSettings(null);
        setChatbotId(null);
        setLoadingSettings(true);
        const s = await chatbotService.getSettings(selectedProjectId === 'TEAM' ? undefined : selectedProjectId);
        if (s) {
          setSettings(s);
          setChatbotId(s.id);
        } else {
          // Explicitly reflect that no chatbot exists for this scope
          setSettings(null);
          setChatbotId(null);
        }
      } catch {
        // ignore
      } finally {
        setLoadingSettings(false);
      }
    })();
  }, [selectedProjectId]);

  useEffect(() => {
    (async () => {
      try {
        const list = await projectService.list();
        setProjects(list);
      } catch {
        // ignore
      }
    })();
  }, []);

  const handleCreate = async () => {
    try {
      setCreating(true);
      const s = await chatbotService.createSettings(selectedProjectId === 'TEAM' ? undefined : selectedProjectId);
      setSettings(s);
      setChatbotId(s.id);
      toast('Chatbot created', { description: `ID: ${s.id}` });
    } catch (e) {
      toast('Failed to create chatbot', { description: e instanceof Error ? e.message : 'Unknown error' });
    } finally {
      setCreating(false);
    }
  };

  const handleSave = async () => {
    if (!settings) return;
    try {
      setSaving(true);
      const updated = await chatbotService.updateSettings(settings);
      setSettings(updated);
      toast('Chatbot updated');
    } catch (e) {
      toast('Failed to update chatbot', { description: e instanceof Error ? e.message : 'Unknown error' });
    } finally {
      setSaving(false);
    }
  };

  const updateStyle = <K extends keyof ChatStyleConfig['styles']>(key: K, value: ChatStyleConfig['styles'][K]) => {
    if (!settings) return;
    setSettings({ ...settings, styles: { ...settings.styles, [key]: value } });
  };

  const addInitialMessage = () => {
    if (!settings) return;
    setSettings({ ...settings, initialMessages: [...settings.initialMessages, { message: '', instructions: '' }] });
  };

  const updateInitialMessage = (index: number, key: 'message' | 'instructions', value: string) => {
    if (!settings) return;
    const next = settings.initialMessages.map((m, i) => (i === index ? { ...m, [key]: value } : m));
    setSettings({ ...settings, initialMessages: next });
  };

  const removeInitialMessage = (index: number) => {
    if (!settings) return;
    const next = settings.initialMessages.filter((_, i) => i !== index);
    setSettings({ ...settings, initialMessages: next });
  };

  const addBubbleMessage = () => {
    if (!settings) return;
    setSettings({ ...settings, bubbleMessages: [...settings.bubbleMessages, { message: '', instructions: '' }] });
  };

  const updateBubbleMessage = (index: number, key: 'message' | 'instructions', value: string) => {
    if (!settings) return;
    const next = settings.bubbleMessages.map((m, i) => (i === index ? { ...m, [key]: value } : m));
    setSettings({ ...settings, bubbleMessages: next });
  };

  const removeBubbleMessage = (index: number) => {
    if (!settings) return;
    const next = settings.bubbleMessages.filter((_, i) => i !== index);
    setSettings({ ...settings, bubbleMessages: next });
  };

  const addHiddenPath = () => {
    if (!settings) return;
    const current = settings.styles.hidden_paths ?? [];
    updateStyle('hidden_paths', [...current, ""]);
  };

  const updateHiddenPath = (index: number, value: string) => {
    if (!settings) return;
    const current = settings.styles.hidden_paths ?? [];
    const next = [...current];
    next[index] = value;
    updateStyle('hidden_paths', next);
  };

  const removeHiddenPath = (index: number) => {
    if (!settings) return;
    const current = settings.styles.hidden_paths ?? [];
    const next = current.filter((_, i) => i !== index);
    updateStyle('hidden_paths', next);
  };

  const copyText = async (text: string) => {
    await navigator.clipboard.writeText(text);
    toast('Copied to clipboard');
  };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!settings) return;
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'image/svg+xml' && file.type !== 'image/png') {
      toast('Only SVG or PNG files are allowed');
      return;
    }
    const maxBytes = 300 * 1024;
    if (file.size > maxBytes) {
      toast('Image must be smaller than 300 KB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setSettings({ ...settings, logo: dataUrl });
    };
    reader.readAsDataURL(file);
  };

  const handleWelcomeLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!settings) return;
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'image/svg+xml' && file.type !== 'image/png') {
      toast('Only SVG or PNG files are allowed');
      return;
    }
    const maxBytes = 300 * 1024;
    if (file.size > maxBytes) {
      toast('Image must be smaller than 300 KB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setSettings({ ...settings, welcomeLogo: dataUrl });
    };
    reader.readAsDataURL(file);
  };

  const bubbleScript = `<script>
  (function(){if(!window.recallio||window.recallio("getState")!=="initialized"){window.recallio=(...arguments)=>{if(!window.recallio.q){window.recallio.q=[]}window.recallio.q.push(arguments)};window.recallio=new Proxy(window.recallio,{get(target,prop){if(prop==="q"){return target.q}return(...args)=>target(prop,...args)}})}const onLoad=function(){const script=document.createElement("script");script.src="https://app.recallio.ai/chat/recallio-embed.min.js";script.id="#CHATBOT_ID#";document.body.appendChild(script)};if(document.readyState==="complete"){onLoad()}else{window.addEventListener("load",onLoad)}})();
  </script>`;
  const iframeHtml = `<iframe
    src="https://app.recallio.ai/chat/#CHATBOT_ID#"
    width="100%"
    style="height: 100%; min-height: 700px"
    frameborder="0"
></iframe>`;

  return (
    <PageLayout
      title="Chatbot"
      description="Create and embed your Recallio AI agent on your site"
      showBackToDashboard
    >
      <div className="space-y-6">
        {projects.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Project</CardTitle>
              <CardDescription>
                Select a project to create a project-scoped chatbot. Default is Team.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="min-w-[260px]">
                <Select value={selectedProjectId} onValueChange={(v) => setSelectedProjectId(v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Team" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TEAM">Team</SelectItem>
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        )}
        <Card>
          <CardHeader>
            <CardTitle>Chatbot</CardTitle>
            <CardDescription>
              Create a chatbot agent to power on-site support and interactions.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingSettings ? (
              <div className="text-sm text-muted-foreground">Loading settings…</div>
            ) : !chatbotId ? (
              <Button onClick={handleCreate} disabled={creating} className="btn-primary">
                {creating ? 'Creating…' : 'Create a chatbot'}
              </Button>
            ) : (
              <div className="flex items-center gap-3">
                <Label className="text-sm text-muted-foreground">Chatbot ID</Label>
                <Input value={chatbotId} readOnly className="max-w-md" />
              </div>
            )}
          </CardContent>
        </Card>

        {chatbotId && (
          <Card>
            <CardHeader>
              <CardTitle>Embed</CardTitle>
              <CardDescription>Choose how to add the agent to your website.</CardDescription>
            </CardHeader>
            <CardContent>
              <RadioGroup value={embedChoice} onValueChange={(v) => setEmbedChoice(v as EmbedChoice)} className="grid gap-4 md:grid-cols-2">
                <Label htmlFor="bubble" className="border rounded-lg p-4 cursor-pointer hover:bg-accent/20">
                  <div className="flex items-start gap-3">
                    <RadioGroupItem id="bubble" value="bubble" className="mt-1" />
                    <div>
                      <div className="font-medium">Embed a chat bubble</div>
                      <div className="text-xs text-muted-foreground">Recommended</div>
                      <p className="mt-2 text-sm text-muted-foreground">
                        Embed a chat bubble on your website. Allows you to use all the advanced features of the agent. Explore the docs.
                      </p>
                    </div>
                  </div>
                </Label>

                <Label htmlFor="iframe" className="border rounded-lg p-4 cursor-pointer hover:bg-accent/20">
                  <div className="flex items-start gap-3">
                    <RadioGroupItem id="iframe" value="iframe" className="mt-1" />
                    <div>
                      <div className="font-medium">Embed the iframe directly</div>
                      <p className="mt-2 text-sm text-muted-foreground">
                        Add the agent anywhere on your website.
                      </p>
                    </div>
                  </div>
                </Label>
              </RadioGroup>
            </CardContent>
          </Card>
        )}

        {chatbotId && (
          <Card>
            <CardHeader>
              <CardTitle>Configuration</CardTitle>
              {embedChoice === 'bubble' ? (
                <CardDescription>
                  Paste this code on your site (e.g., www.recallio.ai) to install the chat widget and enable AI-powered support.
                </CardDescription>
              ) : (
                <CardDescription>
                  Use this code to embed the iframe.
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              {embedChoice === 'bubble' ? (
                <div className="space-y-3">
                  <Textarea value={bubbleScript.replace('#CHATBOT_ID#', chatbotId)} readOnly className="font-mono text-sm h-40" />
                  <div>
                    <Button variant="outline" onClick={() => copyText(bubbleScript.replace('#CHATBOT_ID#', chatbotId))}>
                      <Copy className="mr-2 h-4 w-4" /> Copy
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <Textarea value={iframeHtml.replace('#CHATBOT_ID#', chatbotId)} readOnly className="font-mono text-sm h-40" />
                  <div>
                    <Button variant="outline" onClick={() => copyText(iframeHtml.replace('#CHATBOT_ID#', chatbotId))}>
                      <Copy className="mr-2 h-4 w-4" /> Copy
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {settings && (
          <Card>
            <CardHeader>
              <CardTitle>Identity verification</CardTitle>
              <CardDescription>
                Secure your AI Agent by generating an HMAC for each logged-in user and sending it to Recallio.AI. This ensures requests come from verified users only.
                <br />- You'll need to generate an HMAC on your server for each logged-in user and send it to Recallio.AI.
                <br />- You'll need your secret key to add identity verification to your site or app.
                <br />- You can pass identity once confirmed via : window.recallio.identify({'{'}user_id: "user-123", user_hash: "ea5b3446bc6288ff021a6ad86ede4fad3cd594b4cb2ec3ae1576964021c76137"{'}'})
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Secret Key</Label>
                  <div className="flex items-center gap-2">
                    <Input value={settings.hmacKey || ''} readOnly className="font-mono" />
                    <Button variant="outline" onClick={() => copyText(settings.hmacKey || '')}>
                      <Copy className="mr-2 h-4 w-4" /> Copy
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Server-side snippet</Label>
                  <Textarea
                    readOnly
                    className="font-mono text-sm h-40"
                    value={`const crypto = require('crypto');

const secret = '•••••••••'; // Your verification secret key
const userId = current_user.id // A string UUID to identify your user

const hash = crypto.createHmac('sha256', secret).update(userId).digest('hex');`}
                  />
                  <div>
                    <Button variant="outline" onClick={() => copyText(`const crypto = require('crypto');

const secret = '•••••••••'; // Your verification secret key
const userId = current_user.id // A string UUID to identify your user

const hash = crypto.createHmac('sha256', secret).update(userId).digest('hex');`)}>
                      <Copy className="mr-2 h-4 w-4" /> Copy
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {settings && (
          <Card>
            <CardHeader>
              <CardTitle>Customization</CardTitle>
              <CardDescription>Configure initial messages and appearance.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3">
                      <Switch checked={!!settings.styles.auto_open_bubble_messages} onCheckedChange={(v) => updateStyle('auto_open_bubble_messages', v)} />
                      <span className="font-medium">Auto-open welcome & bubble messages</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Label className="whitespace-nowrap">Auto-open after (seconds)</Label>
                      <Input className="w-28" type="number" value={settings.styles.auto_open_bubble_messages_after} onChange={(e) => updateStyle('auto_open_bubble_messages_after', Number(e.target.value))} />
                    </div>
                  </div>
                </div>

                <div>
                  <div className="space-y-3 mb-6">
                    <div className="space-y-2">
                      <Label>Welcome message</Label>
                      <Textarea
                        value={settings.welcomeMessage ?? ''}
                        onChange={(e) => setSettings({ ...settings, welcomeMessage: e.target.value })}
                        placeholder="Welcome message shown above the bubble"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Welcome logo (SVG or PNG, &lt; 300 KB)</Label>
                      <div className="flex items-center gap-4">
                        <Input type="file" accept="image/svg+xml,image/png" onChange={handleWelcomeLogoChange} />
                        {settings.welcomeLogo && (
                          <img src={settings.welcomeLogo} alt="Welcome logo preview" className="h-12 w-auto rounded border" />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="font-medium mb-2">Bubble Messages</div>
                  <div className="space-y-3">
                    {settings.bubbleMessages.map((msg, idx) => (
                      <div key={idx} className="border rounded-lg p-4 space-y-3">
                        <Label>Message</Label>
                        <Input
                          value={msg.message}
                          onChange={(e) => updateBubbleMessage(idx, 'message', e.target.value)}
                          placeholder={`Bubble message #${idx + 1}`}
                        />
                        <Label>Instructions</Label>
                        <Textarea
                          value={msg.instructions}
                          onChange={(e) => updateBubbleMessage(idx, 'instructions', e.target.value)}
                          placeholder="Instructions (optional)"
                        />
                        <div className="flex justify-end">
                          <Button variant="ghost" onClick={() => removeBubbleMessage(idx)} aria-label="Remove">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    <Button variant="outline" onClick={addBubbleMessage} className="gap-2">
                      <Plus className="h-4 w-4" /> Add bubble message
                    </Button>
                  </div>
                </div>

                <div>
                  <div className="font-medium mb-2">Suggested Questions</div>
                  <div className="space-y-3">
                    {settings.initialMessages.map((msg, idx) => (
                      <div key={idx} className="border rounded-lg p-4 space-y-3">
                        <Label>Message</Label>
                        <Input
                          value={msg.message}
                          onChange={(e) => updateInitialMessage(idx, 'message', e.target.value)}
                          placeholder={`Initial message #${idx + 1}`}
                        />
                        <Label>Instructions</Label>
                        <Textarea
                          value={msg.instructions}
                          onChange={(e) => updateInitialMessage(idx, 'instructions', e.target.value)}
                          placeholder="Instructions (optional)"
                        />
                        <div className="flex justify-end">
                          <Button variant="ghost" onClick={() => removeInitialMessage(idx)} aria-label="Remove">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    <Button variant="outline" onClick={addInitialMessage} className="gap-2">
                      <Plus className="h-4 w-4" /> Add message
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div className="font-medium">Theme</div>
                    <RadioGroup value={settings.styles.theme} onValueChange={(v) => updateStyle('theme', v as 'dark' | 'light')} className="grid grid-cols-2 gap-3">
                      <Label htmlFor="theme-dark" className="border rounded-lg p-3 cursor-pointer">
                        <div className="flex items-center gap-2">
                          <RadioGroupItem id="theme-dark" value="dark" />
                          <span>Dark</span>
                        </div>
                      </Label>
                      <Label htmlFor="theme-light" className="border rounded-lg p-3 cursor-pointer">
                        <div className="flex items-center gap-2">
                          <RadioGroupItem id="theme-light" value="light" />
                          <span>Light</span>
                        </div>
                      </Label>
                    </RadioGroup>
                  </div>

                  <div className="space-y-3">
                    <div className="font-medium">Align chat button</div>
                    <RadioGroup value={settings.styles.align_chat_button} onValueChange={(v) => updateStyle('align_chat_button', v as 'left' | 'right')} className="grid grid-cols-2 gap-3">
                      <Label htmlFor="align-left" className="border rounded-lg p-3 cursor-pointer">
                        <div className="flex items-center gap-2">
                          <RadioGroupItem id="align-left" value="left" />
                          <span>Left</span>
                        </div>
                      </Label>
                      <Label htmlFor="align-right" className="border rounded-lg p-3 cursor-pointer">
                        <div className="flex items-center gap-2">
                          <RadioGroupItem id="align-right" value="right" />
                          <span>Right</span>
                        </div>
                      </Label>
                    </RadioGroup>
                  </div>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Logo (SVG or PNG, &lt; 300 KB)</Label>
                  <div className="flex items-center gap-4">
                    <Input type="file" accept="image/svg+xml,image/png" onChange={handleLogoChange} />
                    {settings.logo && (
                      <img src={settings.logo} alt="Logo preview" className="h-12 w-auto rounded border" />
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Brand name</Label>
                    <Input value={settings.styles.brand_name} onChange={(e) => updateStyle('brand_name', e.target.value)} placeholder="Brand name" />
                  </div>
                  <div className="space-y-2">
                    <Label>Primary color</Label>
                    <Input type="color" value={settings.styles.primary_color} onChange={(e) => updateStyle('primary_color', e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Secondary color</Label>
                    <Input type="color" value={settings.styles.secondary_color} onChange={(e) => updateStyle('secondary_color', e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Accent color</Label>
                    <Input type="color" value={settings.styles.accent_color} onChange={(e) => updateStyle('accent_color', e.target.value)} />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Message placeholder</Label>
                    <Input value={settings.styles.message_placeholder} onChange={(e) => updateStyle('message_placeholder', e.target.value)} placeholder="Message..." />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Tagline</Label>
                    <Input value={settings.styles.tagline} onChange={(e) => updateStyle('tagline', e.target.value)} placeholder="You can ask me anything" />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Welcome message</Label>
                    <Input value={settings.styles.welcome_message} onChange={(e) => updateStyle('welcome_message', e.target.value)} placeholder="Ask anything about transactions, listings..." />
                  </div>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>Customize the way the agent answers questions.</Label>
                  <Textarea
                    value={settings.customInstructions ?? ''}
                    onChange={(e) => setSettings({ ...settings, customInstructions: e.target.value })}
                    placeholder="Describe how the agent should answer questions..."
                    className="h-48"
                  />
                </div>

                <div className="flex justify-end">
                  <Button onClick={handleSave} disabled={saving} className="btn-primary">
                    {saving ? 'Saving…' : 'Save changes'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </PageLayout>
  );
};

export default Chatbot;


